import { Box, Button, TextField, Typography } from "@material-ui/core";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useNotify, useRedirect } from "react-admin";

import CustomCard from "../../../components/CustomCard";
import CustomSelect from "../../../components/CustomSelect";
import Loader from "../../../components/Loader";
import Spacing from "../../../components/Spacing";
import useCurrentUser from "../../../hooks/useCurrentUser";
import useDisabledOnLoading from "../../../hooks/useDisabledOnLoading";
import { AppResource } from "../../../types/resource.types";
import { academyPackageDocRef, sportTypesCollRef } from "../../../utils/referencies";
import { applyUpdateTimestamp } from "../../../utils/timestamp";
import LocalizedRichText from "../../Settings/LocalizedRichText";

const AcademyPackageUpdate: FC<any> = (props) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const { user } = useCurrentUser();
  const benefits = useRef({ en: "", ar: "" });

  const [fetchedPackage, setFetchedPackage] = useState<undefined | Record<string, any>>(undefined);
  const [fetchedPackageError, setFetchedPackageError] = useState<string | null>(null);
  const [fetchedPackageLoader, setFetchedPackageLoader] = useState(false);

  const [isDisabled, setIsDisabled] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [richTextError, setRichTextError] = useState(true);

  const [sportTypesList, setSportTypesList] = useState<any[]>([]);
  const { disabledStyles } = useDisabledOnLoading(submitLoading);

  const handleBenefitsChange = useCallback((value: any) => {
    benefits.current = value;
  }, []);

  const handleRichTextError = useCallback((error) => {
    setRichTextError(error);
  }, []);

  const handleSelectSportTypes = (selectedItems: any) => {
    setFetchedPackage((prevSettings: any) => {
      return {
        ...prevSettings,
        sportTypeId: selectedItems,
      };
    });
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);
    try {
      await academyPackageDocRef(user.id!, props.id).update(
        applyUpdateTimestamp({
          ...fetchedPackage,
          fees: +fetchedPackage?.fees,
          days: +fetchedPackage?.days,
          benefits: benefits.current,
        })
      );
      setSubmitLoading(false);
      notify("Package has been updated successfully", "success");
      redirect(`/${AppResource.packages}`);
    } catch (ex: any) {
      console.log(ex.message);
      setSubmitLoading(false);
      notify("Error on updating package", "error");
    }
  };

  useEffect(() => {
    const fetchPackage = async () => {
      setFetchedPackageLoader(true);
      try {
        const packageSnapshot = await academyPackageDocRef(user.id!, props.id).get();
        if (packageSnapshot.exists) {
          const packageData = packageSnapshot.data();
          if (packageData) {
            setFetchedPackage({
              ...packageData,
              fees: packageData.fees.toString(),
              days: packageData.days.toString(),
            });
          }
        }
        setFetchedPackageLoader(false);
      } catch (ex: any) {
        console.log(ex.message);
        setFetchedPackageError("Erro on feching data");
        setFetchedPackageLoader(false);
      }
    };

    fetchPackage();
  }, [props.id, user?.id]);

  useEffect(() => {
    if (fetchedPackage) {
      setIsDisabled(
        !!(
          !fetchedPackage.days ||
          !fetchedPackage.fees ||
          fetchedPackageError ||
          !fetchedPackage.title.ar ||
          !fetchedPackage.title.en ||
          Number(fetchedPackage.days) < 1 ||
          Number(fetchedPackage.fees) < 0 ||
          !fetchedPackage.sportTypeId ||
          richTextError
        )
      );
    }
  }, [fetchedPackage, fetchedPackageError, richTextError]);

  useEffect(() => {
    const fetchSportTypes = async () => {
      try {
        const sportTypeSnapshots = await sportTypesCollRef.get();
        if (sportTypeSnapshots.size) {
          const data = sportTypeSnapshots.docs.map((doc) => ({
            id: doc.id,
            name: doc.data().title.en,
          }));
          setSportTypesList(data.filter((doc) => user?.sportTypeIds.includes(doc.id)));
        }
      } catch (ex: any) {
        console.log(ex.message);
      }
    };

    fetchSportTypes();
  }, [user?.sportTypeIds]);

  return (
    <Loader loading={fetchedPackageLoader}>
      {fetchedPackage && (
        <Box style={disabledStyles}>
          <CustomCard title="Details" width="50%">
            <Spacing itemStyle={{ marginBottom: 12 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Title (En)"
                value={fetchedPackage.title?.en}
                onChange={(e) =>
                  setFetchedPackage((prevState: any) => {
                    return { ...prevState, title: { ...prevState.title, en: e.target.value } };
                  })
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Title (Ar)"
                value={fetchedPackage.title?.ar}
                onChange={(e) =>
                  setFetchedPackage((prevState: any) => {
                    return { ...prevState, title: { ...prevState.title, ar: e.target.value } };
                  })
                }
              />
            </Spacing>
            <Spacing itemStyle={{ marginBottom: 12 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Days"
                type="number"
                value={fetchedPackage.days}
                onChange={(e) =>
                  setFetchedPackage((prevState: any) => {
                    return { ...prevState, days: e.target.value };
                  })
                }
                inputProps={{
                  min: 1,
                }}
              />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Fees"
                type="number"
                value={fetchedPackage.fees}
                onChange={(e) =>
                  setFetchedPackage((prevState: any) => {
                    return { ...prevState, fees: e.target.value };
                  })
                }
                inputProps={{
                  min: 0,
                }}
              />
            </Spacing>
            {sportTypesList.length && (
              <CustomSelect
                selectedItem={fetchedPackage.sportTypeId}
                handleSelectItem={handleSelectSportTypes}
                labelTitle="Sport Type"
                listItems={sportTypesList}
              />
            )}
          </CustomCard>

          <CustomCard title="Benefits" width="50%">
            <LocalizedRichText
              onChange={handleBenefitsChange}
              hasError={handleRichTextError}
              initialValue={fetchedPackage.benefits}
            />
          </CustomCard>

          <Button variant="contained" color="primary" disabled={isDisabled} onClick={handleSubmit}>
            Save
          </Button>
          <Box mt={2}>
            {fetchedPackage.days && Number(fetchedPackage.days) < 1 && (
              <Typography color="error" variant="body2">
                Invalid Days
              </Typography>
            )}
            {fetchedPackage.fees && Number(fetchedPackage.fees) < 0 && (
              <Typography color="error" variant="body2">
                Invalid Fees
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Loader>
  );
};

export default AcademyPackageUpdate;
